package com.schoolconnect.app.feature.schoolconfig

/**
 * Attendance / Exam thresholds must cover 0–100% with no gaps or overlaps.
 */
internal fun validateThresholdCoverage(
    rows: List<ThresholdDraft>,
    sectionLabel: String,
    requireGrade: Boolean = false
): String {
    val coverageMessage = "$sectionLabel thresholds must cover 0%–100% with no gaps or overlaps."
    if (rows.isEmpty()) {
        return coverageMessage
    }

    val ranges = mutableListOf<Pair<Int, Int>>()
    rows.forEach { row ->
        if (row.level.trim().isBlank()) {
            return "$sectionLabel: each row needs a level name."
        }
        if (requireGrade && row.grade.trim().isBlank()) {
            return "$sectionLabel: each row needs a grade."
        }
        val min = row.minimum.trim().toIntOrNull()
        val max = row.maximum.trim().toIntOrNull()
        if (min == null || max == null) {
            return "$sectionLabel: each row needs valid minimum and maximum percentages."
        }
        if (min < 0 || max > 100 || min > max) {
            return "$sectionLabel: percentages must be between 0 and 100 with minimum ≤ maximum."
        }
        ranges += min to max
    }

    val sorted = ranges.sortedBy { it.first }
    if (sorted.first().first != 0 || sorted.last().second != 100) {
        return coverageMessage
    }
    for (index in 0 until sorted.lastIndex) {
        val current = sorted[index]
        val next = sorted[index + 1]
        if (next.first <= current.second || next.first != current.second + 1) {
            return coverageMessage
        }
    }
    return ""
}

internal data class SchoolConfigThresholdErrors(
    val attendance: String = "",
    val exams: String = ""
) {
    val hasAny: Boolean get() = attendance.isNotBlank() || exams.isNotBlank()
}

internal fun validateSchoolConfigThresholds(
    attendance: List<ThresholdDraft>,
    exams: List<ThresholdDraft>
): SchoolConfigThresholdErrors = SchoolConfigThresholdErrors(
    attendance = validateThresholdCoverage(attendance, "Attendance"),
    exams = validateThresholdCoverage(exams, "Exam", requireGrade = true)
)
